/** The user record's section registry (plan "Page anatomy" → Cards): every catalog field
 *  key → its card (Contacts · Reachability · Identity · Access · Origin · Technical), its
 *  position and its block (the Editable fields, the muted "From the app / OTA" vendor
 *  block, or the System block of derived / provenance values).
 *
 *  The same shape as the booking and order registries (`RegistryEntry`), so the records
 *  core lays all three out alike. A person has no lifecycle: `stage` and `facet` are the
 *  card (`UserRecordSection`).
 *
 *  Contract (registry.contract.test.ts): the keys are exactly the spec enum
 *  `UserRecordFieldKey`, so a field the backend adds fails console CI until it is placed
 *  here. */
import { USER_RECORD_SECTIONS, type UserRecordSection } from '@/api/userRecord';
import type { FieldBlockKind, FieldRegistry, RegistryEntry } from '@/features/records/views/types';

export type UserRegistryEntry = RegistryEntry<UserRecordSection, UserRecordSection>;

function at(section: UserRecordSection, block: FieldBlockKind, order: number): UserRegistryEntry {
  return { stage: section, facet: section, block, order };
}

const E = 'editable';
const V = 'vendor';
const S = 'system';

export const USER_RECORD_REGISTRY = {
  // ---------------------------------------------------------------- Contacts
  contacts: at('contacts', E, 10),
  freeze_email: at('contacts', E, 20),
  freeze_phone: at('contacts', E, 30),
  email_verified: at('contacts', S, 40),

  // ------------------------------------------------------------ Reachability
  allow_email_nf: at('reachability', E, 10),
  allow_sms_nf: at('reachability', E, 20),
  allow_whatsapp_nf: at('reachability', E, 30),

  // ---------------------------------------------------------------- Identity
  first_name: at('identity', E, 10),
  last_name: at('identity', E, 20),
  lang: at('identity', E, 30),
  country: at('identity', E, 40),
  date_of_birth: at('identity', E, 50),
  passport_number: at('identity', E, 60),
  address: at('identity', E, 70),
  tags: at('identity', E, 80),

  // ------------------------------------------------------------------ Access
  is_active: at('access', E, 10),
  is_staff: at('access', E, 20),

  // ------------------------------------------------------------------ Origin
  ota: at('origin', V, 10),
  app_platform: at('origin', S, 20),
  email_is_fake: at('origin', S, 30),

  // --------------------------------------------------------------- Technical
  twilio_identity: at('technical', S, 10),
} as const satisfies FieldRegistry<UserRecordSection, UserRecordSection>;

/** Every catalog key the console places — the spec enum `UserRecordFieldKey`. */
export type UserRecordFieldKey = keyof typeof USER_RECORD_REGISTRY;

export type UserRegistry = Readonly<Record<string, UserRegistryEntry>>;

/** Card titles, in page order. */
export const USER_SECTION_TITLES: Record<UserRecordSection, string> = {
  contacts: 'Contacts',
  reachability: 'Reachability',
  identity: 'Identity',
  access: 'Access',
  origin: 'Origin',
  technical: 'Technical',
};

/** The keys grouped by card, ordered by card then position. Pure. */
export function userPlacements(registry: UserRegistry): Map<UserRecordSection, string[]> {
  const index = (s: UserRecordSection) => USER_RECORD_SECTIONS.indexOf(s);
  const keys = Object.keys(registry).sort((a, b) => {
    const x = registry[a]!;
    const y = registry[b]!;
    return index(x.stage) - index(y.stage) || x.order - y.order;
  });
  const out = new Map<UserRecordSection, string[]>();
  for (const key of keys) {
    const section = registry[key]!.stage;
    const list = out.get(section);
    if (list) list.push(key);
    else out.set(section, [key]);
  }
  return out;
}
